/**
 * ImageAssetPickerDialog — pick a web-ready client image asset to insert into
 * the blog editor.
 *
 * Browses the workspace's client image asset library (web-ready state only) via
 * the existing useClientImageAssetsList hook, with a debounced search box and a
 * category chip filter. Clicking a tile copies the asset to a durable public URL
 * through clientImageAssetsService.selectForEditor — the signed list URL (which
 * expires) is never inserted into the document — then reports the copied URL +
 * alt text to the caller via onPick and closes.
 *
 * Tenant is read from useTenantStore().currentTenant (same contract as the rest
 * of the editor page). With no tenant selected the dialog renders a disabled
 * empty state.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  ImageIcon,
  Loader2,
  Search,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useTenantStore } from '@/stores/useTenantStore';
import { useClientImageAssetsList } from '@/hooks/useClientImageAssetsList';
import {
  clientImageAssetsService,
  type ClientImageAssetListItem,
} from '@/services/clientImageAssetsService';

const PAGE_SIZE = 24;

// Mirrors the client_image_assets category CHECK constraint.
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'product', label: 'Product' },
  { value: 'project', label: 'Project' },
  { value: 'location', label: 'Location' },
  { value: 'team', label: 'Team' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'logo', label: 'Logo' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Other' },
];

export interface ImageAssetPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (sel: { url: string; alt: string }) => void;
}

function FileTile({ extension }: { extension?: string | null }) {
  const label = extension ? extension.toUpperCase() : 'FILE';
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-muted text-muted-foreground">
      <FileText className="h-7 w-7" />
      <span className="text-xs font-semibold tracking-wide">{label}</span>
    </div>
  );
}

function PickerTile({
  item,
  loading,
  disabled,
  imgErrored,
  onImgError,
  onSelect,
}: {
  item: ClientImageAssetListItem;
  loading: boolean;
  disabled: boolean;
  imgErrored: boolean;
  onImgError: (assetId: string) => void;
  onSelect: (assetId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      disabled={disabled}
      title={item.filename}
      data-testid="asset-tile"
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition-colors',
        'hover:border-coral/40 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/50',
        'disabled:cursor-not-allowed disabled:opacity-60'
      )}
    >
      <div className="aspect-video w-full overflow-hidden bg-muted">
        {item.url && !imgErrored ? (
          <img
            src={item.url}
            alt={item.alt_text || item.filename}
            loading="lazy"
            onError={() => onImgError(item.id)}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          />
        ) : (
          <FileTile extension={item.extension} />
        )}
      </div>
      <div className="space-y-1.5 p-2.5">
        <p
          className="truncate text-xs font-medium text-foreground"
          title={item.filename}
        >
          {item.filename}
        </p>
        {item.category ? (
          <Badge variant="outline" className="text-[11px]">
            {item.category}
          </Badge>
        ) : null}
      </div>
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-warm-bg/70">
          <Loader2 className="h-6 w-6 animate-spin text-coral" />
        </div>
      ) : null}
    </button>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-border">
          <Skeleton className="aspect-video w-full" />
          <div className="space-y-2 p-2.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-4 w-14 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ImageAssetPickerDialog({
  open,
  onOpenChange,
  onPick,
}: ImageAssetPickerDialogProps) {
  const { currentTenant } = useTenantStore();
  const tenantId = currentTenant || null;

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset all local state each time the dialog opens so it never shows stale
  // results/errors from a prior session.
  useEffect(() => {
    if (open) {
      setSearchInput('');
      setCategory(undefined);
      setOffset(0);
      setSelectingId(null);
      setError(null);
    }
  }, [open]);

  // Any filter change restarts pagination at the first page.
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, category]);

  const listParams = useMemo(
    () => ({
      q: debouncedSearch.trim() || undefined,
      category,
      state: 'web-ready',
      limit: PAGE_SIZE,
      offset,
    }),
    [debouncedSearch, category, offset]
  );

  const { data, isLoading, isFetching, isError, refetch, dataUpdatedAt } =
    useClientImageAssetsList(tenantId, listParams, { enabled: open });

  // Tiles whose thumbnail failed to load (e.g. an expired signed URL). We fall
  // back to the file-type tile for these instead of a broken <img> frame. The
  // set is cleared whenever fresh list data arrives so re-signed URLs get a
  // fresh chance to load.
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    setErroredIds(new Set());
  }, [dataUpdatedAt]);

  const handleImgError = (assetId: string) => {
    setErroredIds((prev) => {
      if (prev.has(assetId)) return prev;
      const next = new Set(prev);
      next.add(assetId);
      return next;
    });
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);

  const handleSelect = async (assetId: string) => {
    if (!tenantId || selectingId) return;
    setSelectingId(assetId);
    setError(null);
    try {
      const copied = await clientImageAssetsService.selectForEditor(
        tenantId,
        assetId
      );
      onPick({ url: copied.url, alt: copied.alt_text ?? '' });
      onOpenChange(false);
    } catch (err) {
      console.error('[ImageAssetPickerDialog] selectForEditor failed:', err);
      setError('Could not add this image. Please try again.');
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose from asset library</DialogTitle>
          <DialogDescription>
            Insert a web-ready image from this workspace&apos;s asset library.
          </DialogDescription>
        </DialogHeader>

        {!tenantId ? (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-2 text-center">
            <ImageIcon className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-base font-semibold text-foreground">
              No workspace selected
            </h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              Select a workspace to browse its image assets.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search + category filter */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search filename, alt text, tags…"
                  className="pl-8"
                  aria-label="Search assets"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setCategory(undefined)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    category === undefined
                      ? 'border-coral bg-coral-light text-coral-dark'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  )}
                >
                  All
                </button>
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategory(opt.value)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      category === opt.value
                        ? 'border-coral bg-coral-light text-coral-dark'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            ) : null}

            {/* Results */}
            <div className="relative max-h-[52vh] overflow-y-auto">
              {/* Background refetch cue: filter/pagination changes keep prior tiles
                  via placeholderData, so isLoading stays false and the skeleton never
                  re-shows — float a spinner over the dimmed grid instead. */}
              {isFetching && !isLoading && items.length > 0 ? (
                <div className="pointer-events-none sticky top-0 z-10 -mb-8 flex justify-center pt-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/90 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </span>
                </div>
              ) : null}
              {isLoading ? (
                <GridSkeleton />
              ) : isError ? (
                <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-destructive/30 bg-destructive/5 text-center">
                  <ImageIcon className="h-10 w-10 text-destructive" />
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Could not load your assets.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : items.length === 0 ? (
                <div className="flex min-h-[30vh] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card text-center">
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                  <h3 className="text-base font-semibold text-foreground">
                    No web-ready assets in your library yet
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Upload and label images to reuse them across your articles.
                  </p>
                  <a
                    href="/assets"
                    className="text-sm font-medium text-teal-dark hover:underline"
                  >
                    Open Image Assets Library
                  </a>
                </div>
              ) : (
                <div
                  className={cn(
                    'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4',
                    isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  {items.map((item) => (
                    <PickerTile
                      key={item.id}
                      item={item}
                      loading={selectingId === item.id}
                      disabled={selectingId !== null}
                      imgErrored={erroredIds.has(item.id)}
                      onImgError={handleImgError}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {!isLoading && !isError && total > 0 ? (
              <div className="flex items-center justify-between border-t border-border pt-3">
                <p className="text-xs text-muted-foreground">
                  Showing {rangeStart.toLocaleString()}–
                  {rangeEnd.toLocaleString()} of {total.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setOffset((prev) => Math.max(0, prev - PAGE_SIZE))
                    }
                    disabled={!hasPrev || selectingId !== null}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                    disabled={!hasNext || selectingId !== null}
                  >
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ImageAssetPickerDialog;
